import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, MapPin, MessageSquare, ShieldCheck, Star, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTL } from "../lib/api";
import { fmtQty4 } from "../lib/format";
import QualityBadge from "../components/QualityBadge";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function SupplierDetail() {
    const { supplierId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [msgOpen, setMsgOpen] = useState(false);
    const [msgSubject, setMsgSubject] = useState("");
    const [msgText, setMsgText] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        api.get(`/suppliers/${supplierId}`).then((r) => setData(r.data)).catch(() => setError(true));
    }, [supplierId]);

    const sendMessage = async () => {
        if (!msgText.trim()) return toast.error("Mesaj yazın");
        setSending(true);
        try {
            const r = await api.post("/messages/conversations", {
                supplier_id: supplierId,
                subject: msgSubject || undefined,
                content: msgText.trim(),
            });
            toast.success("Mesaj gönderildi");
            navigate(`/panel/mesajlar?conv=${r.data.conversation_id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Gönderilemedi");
            setSending(false);
        }
    };

    if (error) {
        return (
            <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="supplier-detail-error">
                <h1 className="font-display text-xl font-bold">Tedarikçi görüntülenemiyor</h1>
                <p className="mt-2 text-sm text-muted-foreground">Bu sayfaya erişim yetkiniz yok veya kayıt mevcut değil.</p>
            </div>
        );
    }

    if (!data) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;

    return (
        <div className="space-y-8" data-testid="supplier-detail-page">
            <Link to="/panel/tedarikciler" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="back-to-suppliers">
                <ArrowLeft className="h-4 w-4" /> Tedarikçilere dön
            </Link>

            <div className="rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-terra-600 to-saffron-400 font-display text-2xl font-extrabold text-white">
                            {data.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="font-display text-2xl font-bold" data-testid="supplier-name">{data.name}</h1>
                                {data.verified && <Badge className="border-0 bg-blue-100 text-blue-800"><BadgeCheck className="mr-1 h-3 w-3" /> Doğrulanmış</Badge>}
                            </div>
                            <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {data.city} • Hizmet: {data.service_regions?.join(", ")}</p>
                            <p className="mt-2 max-w-xl text-sm text-muted-foreground">{data.description}</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-start gap-2">
                        {(data.certs || []).map((c) => <Badge key={c} variant="outline"><ShieldCheck className="mr-1 h-3 w-3" />{c}</Badge>)}
                        {user?.role !== "supplier" && (
                            <Button size="sm" className="bg-terra-600 hover:bg-terra-700 text-white" onClick={() => setMsgOpen(true)} data-testid="supplier-message-button">
                                <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Mesaj Gönder
                            </Button>
                        )}
                    </div>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-4 border-t pt-6 sm:grid-cols-4">
                    {[
                        { label: "Güven Skoru", value: data.trust_score, icon: Star },
                        { label: "Müşteri Puanı", value: data.rating, icon: Star },
                        { label: "Zamanında Teslim", value: `%${data.on_time_rate}`, icon: Timer },
                        { label: "Min. Sipariş", value: fmtTL(data.min_order), icon: ShieldCheck },
                    ].map((m) => (
                        <div key={m.label} className="rounded-xl bg-muted/60 p-4 text-center">
                            <m.icon className="mx-auto h-4 w-4 text-terra-500" />
                            <p className="mt-1 font-display text-xl font-extrabold">{m.value}</p>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.label}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <h2 className="font-display text-xl font-bold">Ürün Kataloğu ({data.offers.length})</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="supplier-products">
                    {data.offers.map((o) => (
                        <Link key={o.offer_id} to={`/panel/urun/${o.product_id}`} className="group overflow-hidden rounded-2xl border bg-card shadow-sm transition-shadow hover:shadow-md" data-testid={`supplier-product-${o.offer_id}`}>
                            <img src={o.image} alt={o.product_name} className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                            <div className="p-4">
                                <p className="font-display text-sm font-bold leading-snug line-clamp-2">{o.product_name} <QualityBadge quality={o.quality} tid={o.offer_id} /></p>
                                <div className="mt-2 flex items-center justify-between">
                                    <span className="font-display font-extrabold text-terra-600">{fmtTL(o.price)} <span className="text-xs font-medium text-muted-foreground">/ {o.unit}</span></span>
                                    <span className={`text-xs ${o.stock < o.moq * 3 ? "font-semibold text-amber-700" : "text-muted-foreground"}`}>{fmtQty4(o.stock, o.unit)} {o.unit}</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
                <DialogContent data-testid="supplier-message-dialog">
                    <DialogHeader><DialogTitle className="font-display">{data.name} — Mesaj Gönder</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Input value={msgSubject} onChange={(e) => setMsgSubject(e.target.value)} placeholder="Konu (örn: Eylül ayı fiyat listesi)" data-testid="supplier-message-subject" />
                        <Textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Mesajınız..." rows={4} data-testid="supplier-message-text" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMsgOpen(false)}>Vazgeç</Button>
                        <Button onClick={sendMessage} disabled={sending} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="supplier-message-send">Gönder</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
